import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ref, get, query, orderByChild, onValue, set } from 'firebase/database';
import { realtimeDb } from '../../firebase';
import {
  FaHome, FaArrowLeft, FaRupeeSign, FaEdit,
  FaMoneyBillWave, FaFileInvoiceDollar, FaSearch,
  FaPrint, FaFileAlt, FaFilePdf, FaDownload
} from 'react-icons/fa';
import { HiOutlineDocumentAdd, HiOutlineDocumentText } from 'react-icons/hi';
import { normalizeCompanyKey, firebaseCompanyKey } from '../../Utils/companyUtils';
import { formatDate } from '../../Utils/dateUtils';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const invoiceCss = `
  .invoice-container { font-family: Arial, sans-serif; padding: 20px; max-width: 210mm; margin: auto; }
  .header { text-align: center; margin-bottom: 20px; }
  .company-name { font-size: 24px; font-weight: bold; text-transform: uppercase; }
  .company-desc { font-size: 18px; font-weight: bold; }
  .company-address { font-size: 14px; font-style: italic; }
  .invoice-title { text-align: center; font-size: 20px; font-weight: bold; text-decoration: underline; margin-bottom: 20px; }
  .bill-no { display: flex; justify-content: space-between; font-size: 14px; font-weight: bold; margin-bottom: 20px; }
  .address-box { display: flex; justify-content: space-between; margin-bottom: 20px; }
  .address-section { width: 48%; font-size: 14px; }
  .address-section p { margin: 5px 0; }
  .work-details { margin-bottom: 20px; font-size: 14px; }
  .work-details p { margin: 5px 0; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 12px; }
  th, td { border: 1px solid black; padding: 8px; text-align: center; }
  th { background-color: #f2f2f2; font-weight: bold; }
  .amount-container { margin-bottom: 20px; font-size: 14px; }
  .amount-row { margin: 5px 0; }
  .total-amount { font-weight: bold; }
  .amount-in-words { margin-bottom: 20px; font-size: 14px; }
  .signature { text-align: right; font-size: 14px; }
  @media print {
    .invoice-container { padding: 10px; }
    body { margin: 0; }
  }
`;

const gatePassCss = `
  .gate-pass-container { font-family: Arial, sans-serif; padding: 20px; max-width: 210mm; margin: auto; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 14px; }
  th, td { border: 1px solid black; padding: 10px; text-align: center; }
  th { background-color: #f2f2f2; font-weight: bold; }
  .text-center { text-align: center; }
  .mb-4 { margin-bottom: 16px; }
  .leading-8 { line-height: 2; }
  .font-extrabold { font-weight: 800; }
  .text-3xl { font-size: 24px; }
  .text-xl { font-size: 18px; }
  .text-lg { font-size: 16px; }
  .font-bold { font-weight: bold; }
  .underline { text-decoration: underline; }
  .grid { display: grid; }
  .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
  .gap-3 { gap: 12px; }
  .flex { display: flex; }
  .justify-between { justify-content: space-between; }
  .mt-10 { margin-top: 40px; }
  @media print {
    .gate-pass-container { padding: 10px; }
    body { margin: 0; }
  }
`;

const modalCss = `
  .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; justify-content: center; align-items: center; z-index: 1000; }
  .modal-content { background: white; padding: 20px; border-radius: 8px; max-width: 90%; max-height: 90vh; overflow-y: auto; width: 100%; }
`;

const DivisionPage = () => {
  const { division } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [company, setCompany] = useState('');
  const [info, setInfo] = useState({ address: '', gst: '', tender: '', email: '' });
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [billingData, setBillingData] = useState({
    totalAmount: 0,
    gstAmount: 0,
    netAmount: 0,
    lastUpdated: null,
    totalBills: 0,
    lastBillNumber: 'N/A'
  });
  const [pendingBillData, setPendingBillData] = useState({
    pendingAmount: 0,
    pendingJobs: 0
  });
  const [kvaPrices, setKvaPrices] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [editInfo, setEditInfo] = useState({ address: '', gst: '', tender: '', email: '' });
  const [invoices, setInvoices] = useState([]);
  const [gatePasses, setGatePasses] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [loadingGatePasses, setLoadingGatePasses] = useState(true);
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');
  const [gatePassSearchTerm, setGatePassSearchTerm] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedGatePass, setSelectedGatePass] = useState(null);
  const [showGatePassModal, setShowGatePassModal] = useState(false);

  // Fetch company and initialize division
  useEffect(() => {
    const passedCompany = location.state?.company;
    const storedCompany = localStorage.getItem('selectedCompany');
    const resolvedCompany = passedCompany || storedCompany || 'Sagar Power Lines';
    const normalizedCompany = normalizeCompanyKey(resolvedCompany);

    if (!normalizedCompany) {
      console.error('Invalid company:', { resolvedCompany, normalizedCompany });
      alert('Invalid company. Please select a valid company.');
      navigate('/admin/dashboard');
      return;
    }

    setCompany(normalizedCompany);

    // Initialize new division in Firebase if it doesn't exist
    const sanitizedCompany = firebaseCompanyKey(normalizedCompany);
    const divisionPath = `companies/${sanitizedCompany}/divisions`;
    const divisionRef = ref(realtimeDb, divisionPath);

    get(divisionRef).then((snapshot) => {
      let divisions = snapshot.exists() ? snapshot.val() : [];
      if (!Array.isArray(divisions)) {
        divisions = divisions ? Object.values(divisions) : [];
      }
      if (!divisions.includes(division)) {
        console.log(`Adding new division "${division}" for company "${normalizedCompany}"`);
        set(ref(realtimeDb, `${divisionPath}/${division}`), true)
          .then(() => {
            console.log(`Division "${division}" initialized successfully.`);
            // Initialize default info and KVA prices for new division
            const infoPath = `companies/${sanitizedCompany}/${division}/info`;
            const defaultInfo = {
              address: 'PLOT NO. B.27, JATH MINI INDUSTRIAL AREA, JATH DIST. SANGLI-416404',
              gst: 'N/A',
              tender: 'N/A',
              email: 'N/A'
            };
            set(ref(realtimeDb, infoPath), defaultInfo)
              .then(() => console.log(`Default info set for division "${division}"`))
              .catch((error) => console.error(`Error setting default info for division "${division}":`, error));

            // Initialize default KVA prices for new division
            const kvaPricesPath = `companies/${sanitizedCompany}/${division}/kvaPrices`;
            const defaultKvaPrices = {
              "100 KVA AMDT": 15000,
              "100 KVA CRGO": 12000,
              "65 KVA AMDT": 10000,
              "65 KVA CRGO": 8000
            };
            set(ref(realtimeDb, kvaPricesPath), defaultKvaPrices)
              .then(() => console.log(`Default KVA prices set for division "${division}"`))
              .catch((error) => console.error(`Error setting default KVA prices for division "${division}":`, error));
          })
          .catch((error) => {
            console.error(`Error initializing division "${division}":`, error);
            alert(`Failed to initialize division "${division}": ${error.message}`);
          });
      }
    }).catch((error) => {
      console.error('Error checking divisions:', error);
      alert(`Error checking divisions: ${error.message}`);
    });
  }, [location.state, division, navigate]);

  // Fetch all data from Firebase
  useEffect(() => {
    const fetchInfo = async () => {
      if (!company || !division) return;

      const normalizedCompany = normalizeCompanyKey(company);
      const sanitizedCompany = firebaseCompanyKey(normalizedCompany);

      setLoadingInfo(true);
      setLoadingBilling(true);
      setLoadingInvoices(true);
      setLoadingGatePasses(true);

      try {
        // Fetch division info
        const infoPath = `companies/${sanitizedCompany}/${division}/info`;
        const infoRef = ref(realtimeDb, infoPath);
        const infoSnapshot = await get(infoRef);

        if (infoSnapshot.exists()) {
          const data = infoSnapshot.val();
          setInfo(data || { address: '', gst: '', tender: '', email: '' });
          setEditInfo(data || { address: '', gst: '', tender: '', email: '' });
        } else {
          setInfo({ address: '', gst: '', tender: '', email: '' });
          setEditInfo({ address: '', gst: '', tender: '', email: '' });
        }

        // Fetch KVA prices
        const kvaPricesPath = `companies/${sanitizedCompany}/${division}/kvaPrices`;
        const kvaPricesRef = ref(realtimeDb, kvaPricesPath);
        const kvaPricesSnapshot = await get(kvaPricesRef);
        const kvaPricesData = kvaPricesSnapshot.exists() ? kvaPricesSnapshot.val() : {};
        setKvaPrices(kvaPricesData);

        // Fetch billing summary with real-time listener
        const billSummaryPath = `companies/${sanitizedCompany}/${division}/billSummary`;
        const billSummaryRef = query(ref(realtimeDb, billSummaryPath), orderByChild('billNumber'));
        onValue(billSummaryRef, (billSummarySnapshot) => {
          let totalAmount = 0;
          let lastBillDate = null;
          let lastBillNumber = 'N/A';
          let billCount = 0;
          const invoicesArray = [];

          if (billSummarySnapshot.exists()) {
            billSummarySnapshot.forEach((childSnapshot) => {
              const bill = childSnapshot.val();
              const grossTotal = parseFloat(bill.grossTotal) || 0;
              const billDate = bill.billDate ? new Date(bill.billDate) : bill.timestamp ? new Date(bill.timestamp) : null;
              const billNum = bill.billNumber || childSnapshot.key;

              totalAmount += grossTotal;
              billCount++;

              if (billDate && (!lastBillDate || billDate > lastBillDate)) {
                lastBillDate = billDate;
                lastBillNumber = billNum;
              }

              const invoiceData = {
                id: childSnapshot.key,
                billNumber: billNum,
                billDate: billDate ? formatDate(billDate) : 'N/A',
                rawBillDate: bill.billDate,
                grossTotal,
                cgst: parseFloat(bill.cgst) || 0,
                sgst: parseFloat(bill.sgst) || 0,
                finalTotal: parseFloat(bill.finalTotal) || grossTotal * 1.18,
                workOrderNo: bill.workOrderNo || 'N/A',
                workOrderDate: bill.workOrderDate || 'N/A',
                timestamp: bill.timestamp || Date.now(),
                items: bill.items || [],
                companyDetails: bill.companyDetails || {},
                clientDetails: bill.clientDetails || {},
                division: bill.division || division
              };

              invoicesArray.unshift(invoiceData);
            });

            setInvoices(invoicesArray);
          }

          const gstAmount = totalAmount * 0.18;
          const netAmount = totalAmount + gstAmount;

          setBillingData({
            totalAmount: Number(totalAmount.toFixed(2)),
            gstAmount: Number(gstAmount.toFixed(2)),
            netAmount: Number(netAmount.toFixed(2)),
            lastUpdated: lastBillDate ? formatDate(lastBillDate) : 'No bills yet',
            totalBills: billCount,
            lastBillNumber
          });

          setLoadingBilling(false);
          setLoadingInvoices(false);
        }, (error) => {
          console.error('Error fetching billing data:', error);
          alert(`Error fetching billing data: ${error.message}`);
          setLoadingBilling(false);
          setLoadingInvoices(false);
        });

        // Fetch pending jobs data with real-time listener
        const registerPath = `companies/${sanitizedCompany}/${division}/register`;
        const registerRef = ref(realtimeDb, registerPath);
        onValue(registerRef, (registerSnapshot) => {
          if (registerSnapshot.exists()) {
            const entries = registerSnapshot.val();
            let pendingAmount = 0;
            let pendingJobsCount = 0;

            Object.values(entries).forEach(entry => {
              if (entry.finalChallanNo && !entry.workBillNo && entry.completed) {
                const kvaType = `${entry.kva} KVA ${entry.type || ''}`.trim();
                const price = parseFloat(kvaPricesData[kvaType] || 0);
                if (price > 0) {
                  pendingAmount += price;
                  pendingJobsCount++;
                }
              }
            });

            setPendingBillData({
              pendingAmount: Number(pendingAmount.toFixed(2)),
              pendingJobs: pendingJobsCount
            });
          } else {
            setPendingBillData({
              pendingAmount: 0,
              pendingJobs: 0
            });
          }
        }, (error) => {
          console.error('Error fetching pending jobs:', error);
          alert(`Error fetching pending jobs: ${error.message}`);
        });

        // Fetch gate passes with real-time listener, ordered by gatePassNumber
        const gatePassSummaryPath = `companies/${sanitizedCompany}/${division}/gatePassSummary`;
        const gatePassSummaryRef = query(ref(realtimeDb, gatePassSummaryPath), orderByChild('gatePassNumber'));
        onValue(gatePassSummaryRef, async (gatePassSnapshot) => {
          if (gatePassSnapshot.exists()) {
            const gatePassData = gatePassSnapshot.val();
            const gatePassesArray = await Promise.all(
              Object.entries(gatePassData).map(async ([key, gp]) => {
                const jobDetails = await Promise.all(
                  Object.entries(gp.jobDetails || {}).map(async ([jobId]) => {
                    const jobRef = ref(realtimeDb, `companies/${sanitizedCompany}/${division}/register/${jobId}`);
                    const jobSnap = await get(jobRef);
                    return jobSnap.exists() ? { ...jobSnap.val(), jobId } : null;
                  })
                ).then(results => results.filter(Boolean));

                const totalOilUsed = jobDetails.reduce((sum, job) => sum + parseFloat(job.usedOil || job.oilCap || 0), 0);

                return {
                  id: key,
                  gatePassNumber: gp.gatePassNumber || `GP_${key}`,
                  gatePassDate: gp.gatePassDate ? formatDate(gp.gatePassDate) : formatDate(new Date(gp.timestamp)),
                  jobDetails: jobDetails || [],
                  totalOilUsed: totalOilUsed.toFixed(2),
                  finalVehicleNo: gp.finalVehicleNo || 'N/A',
                  status: gp.status || 'N/A',
                  timestamp: gp.timestamp || Date.now()
                };
              })
            );
            setGatePasses(gatePassesArray.sort((a, b) => a.gatePassNumber.localeCompare(b.gatePassNumber)));
          } else {
            setGatePasses([]);
          }

          setLoadingGatePasses(false);
        }, (error) => {
          console.error('Error fetching gate passes:', error);
          alert(`Error fetching gate passes: ${error.message}`);
          setLoadingGatePasses(false);
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        alert(`Error fetching data: ${error.message}`);
      } finally {
        setLoadingInfo(false);
      }
    };

    fetchInfo();
  }, [company, division]);

  // Helper function to convert numbers to words
  const numberToWords = (num) => {
    const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
      'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen',
      'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

    const convertChunk = (n) => {
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' hundred' + (n % 100 ? ' and ' + convertChunk(n % 100) : '');
      return '';
    };

    if (num === 0) return 'zero';

    let result = '';
    const crore = Math.floor(num / 10000000);
    if (crore) result += convertChunk(crore) + ' crore ';
    num %= 10000000;

    const lakh = Math.floor(num / 100000);
    if (lakh) result += convertChunk(lakh) + ' lakh ';
    num %= 100000;

    const thousand = Math.floor(num / 1000);
    if (thousand) result += convertChunk(thousand) + ' thousand ';
    num %= 1000;

    if (num) result += convertChunk(num);

    return result.trim();
  };

  // Generate Invoice HTML
  const generateInvoiceHTML = (invoice) => {
    return `
      <div class="invoice-container">
        <div class="header">
          <div class="company-name">${company}</div>
          <div class="company-desc">Transformer Service</div>
          <div class="company-address">Address: ${info.address}</div>
        </div>

        <div class="invoice-title">TAX INVOICE</div>

        <div class="bill-no">
          <div class="bill-info">
            <div class="bill-no">Bill No: ${invoice.billNumber}</div>
          </div>
          <div>Bill Date: ${invoice.billDate}</div>
        </div>

        <div class="address-box">
          <div class="address-section">
            <p><strong>Name:</strong> ${company}</p>
            <p><strong>Address:</strong> ${info.address}</p>
            <p><strong>Email:</strong> ${info.email || 'N/A'}</p>
          </div>
          <div class="address-section">
            <p><strong>Name:</strong> ${invoice.clientDetails.name || 'The Executive'}</p>
            <p><strong>Address:</strong> ${info.address}</p>
            <p><strong>Email:</strong> ${invoice.clientDetails.email || 'N/A'}</p>
            <p><strong>GST No:</strong> ${invoice.clientDetails.gst || info.gst}</p>
          </div>
        </div>

        <div class="work-details">
          <p><strong>Name of work:</strong> Repairing of DT_UPTO_100 KVA Distribution (${division})</p>
          <p><strong>Ref:</strong></p>
          <p>1. Tender Ref: ${info.tender || 'N/A'}</p>
          <p>2. Letter of Empanelment.</p>
          <p>3. EE/DTRR/WO/${invoice.workOrderNo}/${invoice.workOrderDate}</p>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 5%;">Sr No</th>
              <th style="width: 7%;">HSN Code</th>
              <th style="width: 10%;">Description</th>
              <th style="width: 8%;">Job No</th>
              <th style="width: 8%;">Make</th>
              <th style="width: 8%;">Make Sr No</th>
              <th style="width: 6%;">KVA</th>
              <th style="width: 8%;">Material</th>
              <th style="width: 8%;">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            ${invoice.items.map((item, idx) => `
              <tr>
                <td>${idx + 1}</td>
                <td>998719</td>
                <td>Repair of Transformer</td>
                <td>${item.jobNo}</td>
                <td>${item.make}</td>
                <td>${item.makeSrNo}</td>
                <td>${item.kva}</td>
                <td>${item.materialType || 'ALU'}</td>
                <td>${(item.amount || 0).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="amount-container">
          <div class="amount-row">Gross Bill: ₹${invoice.grossTotal.toFixed(2)}</div>
          <div class="amount-row">Add CGST 9%: ₹${invoice.cgst.toFixed(2)}</div>
          <div class="amount-row">Add SGST 9%: ₹${invoice.sgst.toFixed(2)}</div>
          <div class="amount-row total-amount">Total Amount: ₹${invoice.finalTotal.toFixed(2)}</div>
        </div>

        <div class="amount-in-words">
          <p><strong>Total Amount (In Rupees):</strong> ₹${invoice.finalTotal.toFixed(2)}</p>
          <p><strong>Total Amount (In Words):</strong> ${numberToWords(Math.round(invoice.finalTotal))} rupees only</p>
        </div>

        <div class="signature">
          <p>${company}</p>
          <p>Proprietor</p>
        </div>
      </div>
    `;
  };

  // Generate PDF Invoice
  const generatePdfInvoice = (invoice) => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    doc.html(generateInvoiceHTML(invoice), {
      callback: function (doc) {
        doc.save(`Invoice_${invoice.billNumber}.pdf`);
      },
      x: 0,
      y: 0,
      width: 210,
      windowWidth: 794
    });
  };

  // Generate Gate Pass HTML (Single copy)
  const generateGatePassHTML = (gatePass) => {
    return `
      <div class="gate-pass-container">
        <div class="text-center mb-4 leading-8">
          <h1 class="text-3xl font-extrabold uppercase">${company}</h1>
          <div class="text-xl font-extrabold">Transformer Service</div>
          <div class="text-xl font-extrabold">${division || 'Division'} Division Gate Pass</div>
          <div class="text-lg font-bold italic">${info.address}</div>
        </div>

        <div class="text-center mb-4 font-extrabold text-2xl underline">GATE PASS</div>

        <div class="grid grid-cols-2 gap-3 text-lg mb-4 font-bold">
          <div><strong class="font-extrabold">Date:</strong> ${gatePass.gatePassDate || 'N/A'}</div>
          <div><strong class="font-extrabold">Vehicle No:</strong> ${gatePass.finalVehicleNo || 'N/A'}</div>
          <div><strong class="font-extrabold">Challan No:</strong> ${gatePass.gatePassNumber || 'N/A'}</div>
          <div><strong class="font-extrabold">Total Oil:</strong> ${gatePass.totalOilUsed || '0.00'} L</div>
        </div>

        <table>
          <thead>
            <tr>
              <th>SR No</th>
              <th>Job No</th>
              <th>KVA</th>
              <th>Make</th>
              <th>Make Sr No</th>
              <th>Type</th>
              <th>Oil Cap</th>
            </tr>
          </thead>
          <tbody>
            ${gatePass.jobDetails.length ? gatePass.jobDetails.map((row, idx) => `
              <tr>
                <td>${idx + 1}</td>
                <td>${row.jobNo || 'N/A'}</td>
                <td>${row.kva || 'N/A'}</td>
                <td>${row.make || 'N/A'}</td>
                <td>${row.makeSrNo || 'N/A'}</td>
                <td>${row.type || 'N/A'}</td>
                <td>${row.usedOil || row.oilCap || '0.00'}</td>
              </tr>
            `).join('') : `
              <tr>
                <td colspan="7">No data available</td>
              </tr>
            `}
          </tbody>
        </table>

        <div class="flex justify-between text-xl mt-10 font-extrabold">
          <div><strong>Sign of Receiver</strong></div>
          <div><strong>Sign of {company}</strong></div>
        </div>
      </div>
    `;
  };

  // Generate PDF Gate Pass (Single copy)
  const generatePdfGatePass = (gatePass) => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    doc.html(generateGatePassHTML(gatePass), {
      callback: function (doc) {
        doc.save(`GatePass_${gatePass.gatePassNumber}.pdf`);
      },
      x: 0,
      y: 0,
      width: 210,
      windowWidth: 794
    });
  };

  // View invoice details in modal
  const viewInvoiceDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  // View gate pass details in modal
  const viewGatePassDetails = (gatePass) => {
    setSelectedGatePass(gatePass);
    setShowGatePassModal(true);
  };

  // Filter invoices based on bill number
  const filteredInvoices = invoices.filter(invoice =>
    invoice.billNumber.toString().toLowerCase().includes(invoiceSearchTerm.toLowerCase())
  );

  // Filter gate passes based on gate pass number or job number
  const filteredGatePasses = gatePasses.filter(gp =>
    gp.gatePassNumber.toLowerCase().includes(gatePassSearchTerm.toLowerCase()) ||
    gp.jobDetails.some(job => job.jobNo?.toLowerCase().includes(gatePassSearchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-200 p-4 md:p-6">
      <style>{invoiceCss}{gatePassCss}{modalCss}</style>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/dashboard', { state: { company } })}
              className="flex items-center gap-2 bg-white hover:bg-gray-100 text-gray-800 px-4 py-2 rounded-lg shadow-md transition-all duration-300 transform hover:scale-105"
            >
              <FaArrowLeft className="text-blue-600" /> <span className="font-semibold">Back</span>
            </button>
            <h1 className="text-3xl font-bold text-gray-900">{division} – {company}</h1>
          </div>
          <div className="text-sm text-gray-600 font-medium">
            {new Date().toLocaleString('en-IN', { 
              weekday: 'long', 
              hour: '2-digit', 
              minute: '2-digit', 
              hour12: true 
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Side - Quick Actions and Division Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-2xl font-semibold text-gray-800 mb-6 border-b border-gray-200 pb-4">
                Quick Actions
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <button
                  onClick={() => navigate(`/admin/division/${division}/entry`, { state: { company } })}
                  className="bg-white border-2 border-blue-100 rounded-xl p-6 shadow-md hover:shadow-lg hover:border-blue-200 transition-all duration-300 transform hover:scale-105 text-left h-full"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                      <HiOutlineDocumentAdd size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-800">Entry Form</h3>
                      <p className="text-sm text-gray-500">Create new entry passes</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => navigate(`/admin/division/${division}/register`, { state: { company } })}
                  className="bg-white border-2 border-green-100 rounded-xl p-6 shadow-md hover:shadow-lg hover:border-green-200 transition-all duration-300 transform hover:scale-105 text-left h-full"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-full bg-green-100 text-green-600">
                      <HiOutlineDocumentText size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-800">Register</h3>
                      <p className="text-sm text-gray-500">View all entries</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => navigate(`/admin/division/${division}/gp-entry`, { state: { company } })}
                  className="bg-white border-2 border-pink-100 rounded-xl p-6 shadow-md hover:shadow-lg hover:border-pink-200 transition-all duration-300 transform hover:scale-105 text-left h-full"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-full bg-pink-100 text-pink-600">
                      <HiOutlineDocumentAdd size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-800">GP Entry Form</h3>
                      <p className="text-sm text-gray-500">Create gate passes</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => navigate(`/admin/division/${division}/gp-register`, { state: { company } })}
                  className="bg-white border-2 border-purple-100 rounded-xl p-6 shadow-md hover:shadow-lg hover:border-purple-200 transition-all duration-300 transform hover:scale-105 text-left h-full"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                      <HiOutlineDocumentText size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-800">GP Register</h3>
                      <p className="text-sm text-gray-500">View gate passes</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => navigate(`/admin/division/${division}/kva`, { state: { company } })}
                  className="bg-white border-2 border-yellow-100 rounded-xl p-6 shadow-md hover:shadow-lg hover:border-yellow-200 transition-all duration-300 transform hover:scale-105 text-left h-full"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-full bg-yellow-100 text-yellow-600">
                      <FaMoneyBillWave size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-800">KVA Pricing</h3>
                      <p className="text-sm text-gray-500">Manage transformer rates</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => navigate(`/admin/division/${division}/create-bill-summary`, { state: { company } })}
                  className="bg-white border-2 border-red-100 rounded-xl p-6 shadow-md hover:shadow-lg hover:border-red-200 transition-all duration-300 transform hover:scale-105 text-left h-full"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-full bg-red-100 text-red-600">
                      <FaFileInvoiceDollar size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-800">Create Bill</h3>
                      <p className="text-sm text-gray-500">Generate invoice</p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Division Information */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                      <FaHome size={18} />
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-800">Division Information</h3>
                  </div>
                  {!isEditing && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex items-center gap-2 bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-1 rounded-lg shadow-md transition-all duration-300 transform hover:scale-105"
                    >
                      <FaEdit /> <span>Edit</span>
                    </button>
                  )}
                </div>

                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Address</label>
                      <input
                        type="text"
                        name="address"
                        value={editInfo.address}
                        onChange={(e) => setEditInfo({ ...editInfo, address: e.target.value })}
                        className="mt-1 p-2 w-full border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">GST Number</label>
                      <input
                        type="text"
                        name="gst"
                        value={editInfo.gst}
                        onChange={(e) => setEditInfo({ ...editInfo, gst: e.target.value })}
                        className="mt-1 p-2 w-full border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Tender</label>
                      <input
                        type="text"
                        name="tender"
                        value={editInfo.tender}
                        onChange={(e) => setEditInfo({ ...editInfo, tender: e.target.value })}
                        className="mt-1 p-2 w-full border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={editInfo.email}
                        onChange={(e) => setEditInfo({ ...editInfo, email: e.target.value })}
                        className="mt-1 p-2 w-full border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex gap-4">
                      <button
                        onClick={async () => {
                          const normalizedCompany = normalizeCompanyKey(company);
                          const sanitizedCompany = firebaseCompanyKey(normalizedCompany);
                          const infoPath = `companies/${sanitizedCompany}/${division}/info`;
                          const infoData = {
                            address: editInfo.address.trim(),
                            gst: editInfo.gst.trim(),
                            tender: editInfo.tender.trim(),
                            email: editInfo.email.trim()
                          };

                          try {
                            await set(ref(realtimeDb, infoPath), infoData);
                            setInfo(infoData);
                            setIsEditing(false);
                            alert('Division info saved successfully.');
                          } catch (error) {
                            console.error('Error saving info:', error);
                            alert(`Error saving division info: ${error.message}`);
                          }
                        }}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-300"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setEditInfo(info);
                          setIsEditing(false);
                        }}
                        className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors duration-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors duration-300">
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Address</h4>
                      <p className="text-base text-gray-800 font-medium">
                        {info.address || 'Not specified'}
                      </p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors duration-300">
                      <h4 className="text-sm font-medium text-gray-500 mb-2">GST Number</h4>
                      <p className="text-base text-gray-800 font-medium">
                        {info.gst || 'Not specified'}
                      </p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors duration-300">
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Tender</h4>
                      <p className="text-base text-gray-800 font-medium">
                        {info.tender || 'Not specified'}
                      </p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors duration-300">
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Email</h4>
                      <p className="text-base text-gray-800 font-medium">
                        {info.email || 'Not specified'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* All Gate Passes */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-semibold text-gray-800">All Gate Passes</h3>
                <button 
                  onClick={() => navigate(`/admin/division/${division}/gp-entry`, { state: { company } })}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-300"
                >
                  Create New Gate Pass
                </button>
              </div>

              <div className="mb-4 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by gate pass or job number..."
                  className="pl-10 w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  value={gatePassSearchTerm}
                  onChange={(e) => setGatePassSearchTerm(e.target.value)}
                />
              </div>

              {loadingGatePasses ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                </div>
              ) : filteredGatePasses.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  {gatePassSearchTerm ? 'No matching gate passes found' : 'No gate passes available'}
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {filteredGatePasses.map((gp) => (
                    <div key={gp.id} className="space-y-2">
                      <div 
                        className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors duration-300 cursor-pointer"
                        onClick={() => viewGatePassDetails(gp)}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-medium text-gray-800">Gate Pass #{gp.gatePassNumber}</h4>
                            <p className="text-sm text-gray-500">
                              {gp.gatePassDate} | Vehicle: {gp.finalVehicleNo}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-700">
                              Total Oil: {gp.totalOilUsed} L
                            </p>
                            <div className="flex gap-2 mt-2 justify-end">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  generatePdfGatePass(gp);
                                }}
                                className="text-blue-600 hover:text-blue-800 p-1"
                                title="Download PDF"
                              >
                                <FaDownload size={16} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const printWindow = window.open('', '_blank');
                                  printWindow.document.write(`
                                    <!DOCTYPE html>
                                    <html>
                                      <head>
                                        <title>Gate Pass ${gp.gatePassNumber}</title>
                                        <style>${gatePassCss}</style>
                                      </head>
                                      <body>
                                        ${generateGatePassHTML(gp)}
                                        <script>
                                          window.onload = function() {
                                            window.print();
                                            setTimeout(() => window.close(), 500);
                                          }
                                        </script>
                                      </body>
                                    </html>
                                  `);
                                  printWindow.document.close();
                                }}
                                className="text-green-600 hover:text-green-800 p-1"
                                title="Print Gate Pass"
                              >
                                <FaPrint size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Billing Details and Invoices */}
          <div className="lg:col-span-1 space-y-6">
            {/* Billing Overview */}
            {!loadingBilling && (
              <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-600 transform transition-all duration-300 hover:shadow-xl">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 rounded-full bg-blue-100 text-blue-600 animate-pulse">
                    <FaRupeeSign size={24} />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-800">Billing Overview</h3>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-blue-50 p-4 rounded-lg text-center hover:bg-blue-100 transition-colors duration-300">
                    <p className="text-xs text-gray-500 font-medium">Before GST</p>
                    <p className="text-xl font-bold text-blue-800">
                      ₹{billingData.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </p>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg text-center hover:bg-green-100 transition-colors duration-300">
                    <p className="text-xs text-gray-500 font-medium">GST (18%)</p>
                    <p className="text-xl font-bold text-green-800">
                      ₹{billingData.gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </p>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg text-center hover:bg-purple-100 transition-colors duration-300">
                    <p className="text-xs text-gray-500 font-medium">After GST</p>
                    <p className="text-xl font-bold text-purple-800">
                      ₹{billingData.netAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg text-center hover:bg-gray-100 transition-colors duration-300">
                    <p className="text-xs text-gray-500 font-medium">Total Bills</p>
                    <p className="text-xl font-bold text-gray-800">
                      {billingData.totalBills}
                    </p>
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg text-center hover:bg-yellow-100 transition-colors duration-300">
                    <p className="text-xs text-gray-500 font-medium">Pending Amount</p>
                    <p className="text-xl font-bold text-yellow-800">
                      ₹{pendingBillData.pendingAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg text-center hover:bg-gray-100 transition-colors duration-300">
                    <p className="text-xs text-gray-500 font-medium">Pending Jobs</p>
                    <p className="text-xl font-bold text-gray-800">
                      {pendingBillData.pendingJobs}
                    </p>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="text-center md:text-left">
                    <p className="text-sm text-gray-500">Last Bill Number</p>
                    <p className="text-lg font-medium text-gray-800">
                      {billingData.lastBillNumber}
                    </p>
                  </div>

                  <div className="text-center md:text-left">
                    <p className="text-sm text-gray-500">Last Updated</p>
                    <p className="text-lg font-medium text-gray-800">
                      {billingData.lastUpdated}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* All Invoices */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-600">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-semibold text-gray-800">All Invoices</h3>
                <button 
                  onClick={() => navigate(`/admin/division/${division}/create-bill-summary`, { state: { company } })}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-300"
                >
                  Create New Invoice
                </button>
              </div>

              <div className="mb-4 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by bill number..."
                  className="pl-10 w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  value={invoiceSearchTerm}
                  onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                />
              </div>

              {loadingInvoices ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full"></div>
                </div>
              ) : filteredInvoices.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  {invoiceSearchTerm ? 'No matching invoices found' : 'No invoices available'}
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {filteredInvoices.map((invoice) => (
                    <div key={invoice.id} className="space-y-2">
                      <div 
                        className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors duration-300 cursor-pointer"
                        onClick={() => viewInvoiceDetails(invoice)}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-medium text-gray-800">Invoice #{invoice.billNumber}</h4>
                            <p className="text-sm text-gray-500">
                              {invoice.billDate} | WO: {invoice.workOrderNo}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-700">
                              ₹{invoice.finalTotal.toFixed(2)}
                            </p>
                            <div className="flex gap-2 mt-2 justify-end">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  generatePdfInvoice(invoice);
                                }}
                                className="text-blue-600 hover:text-blue-800 p-1"
                                title="Download PDF"
                              >
                                <FaDownload size={16} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const printWindow = window.open('', '_blank');
                                  printWindow.document.write(`
                                    <!DOCTYPE html>
                                    <html>
                                      <head>
                                        <title>Invoice ${invoice.billNumber}</title>
                                        <style>${invoiceCss}</style>
                                      </head>
                                      <body>
                                        ${generateInvoiceHTML(invoice)}
                                        <script>
                                          window.onload = function() {
                                            window.print();
                                            setTimeout(() => window.close(), 500);
                                          }
                                        </script>
                                      </body>
                                    </html>
                                  `);
                                  printWindow.document.close();
                                }}
                                className="text-green-600 hover:text-green-800 p-1"
                                title="Print Invoice"
                              >
                                <FaPrint size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Invoice Detail Modal */}
        {showInvoiceModal && selectedInvoice && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-800">
                  Invoice #{selectedInvoice.billNumber}
                </h2>
                <button
                  onClick={() => setShowInvoiceModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <div dangerouslySetInnerHTML={{ __html: generateInvoiceHTML(selectedInvoice) }} />
              <div className="flex justify-end gap-4 mt-4">
                <button
                  onClick={() => {
                    const printWindow = window.open('', '_blank');
                    printWindow.document.write(`
                      <!DOCTYPE html>
                      <html>
                        <head>
                          <title>Invoice ${selectedInvoice.billNumber}</title>
                          <style>${invoiceCss}</style>
                        </head>
                        <body>
                          ${generateInvoiceHTML(selectedInvoice)}
                          <script>
                            window.onload = function() {
                              window.print();
                              setTimeout(() => window.close(), 500);
                            }
                          </script>
                        </body>
                      </html>
                    `);
                    printWindow.document.close();
                  }}
                  className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-300"
                >
                  <FaPrint /> Print Invoice
                </button>
                <button
                  onClick={() => generatePdfInvoice(selectedInvoice)}
                  className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-300"
                >
                  <FaFilePdf /> Download PDF
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Gate Pass Detail Modal */}
        {showGatePassModal && selectedGatePass && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-800">
                  Gate Pass #{selectedGatePass.gatePassNumber}
                </h2>
                <button
                  onClick={() => setShowGatePassModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <div dangerouslySetInnerHTML={{ __html: generateGatePassHTML(selectedGatePass) }} />
              <div className="flex justify-end gap-4 mt-4">
                <button
                  onClick={() => {
                    const printWindow = window.open('', '_blank');
                    printWindow.document.write(`
                      <!DOCTYPE html>
                      <html>
                        <head>
                          <title>Gate Pass ${selectedGatePass.gatePassNumber}</title>
                          <style>${gatePassCss}</style>
                        </head>
                        <body>
                          ${generateGatePassHTML(selectedGatePass)}
                          <script>
                            window.onload = function() {
                              window.print();
                              setTimeout(() => window.close(), 500);
                            }
                          </script>
                        </body>
                      </html>
                    `);
                    printWindow.document.close();
                  }}
                  className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-300"
                >
                  <FaPrint /> Print Gate Pass
                </button>
                <button
                  onClick={() => generatePdfGatePass(selectedGatePass)}
                  className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-300"
                >
                  <FaFilePdf /> Download PDF
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DivisionPage;